import type { CookieStore, CookieStoreSetOptions } from '@app/utils/cookies/CookieStore'
import { captureException } from '@app/utils/errorReport/errorReport'
import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'

export type CookieSetOptions = {
  expires?: Moment
  httpOnly?: boolean
  sameSite?: CookieStoreSetOptions['sameSite']
  /** Skip checking cookie consent */
  ignoreConsent?: boolean
  secure?: boolean
}

export type CookieInterface = {
  set(name: string, value: string, options?: CookieSetOptions): Promise<void>
  get(name: string): Promise<string | undefined>
  remove(name: string): Promise<void>
}

export type CookieStateItem = {
  name: string
  value: string
  options?: CookieSetOptions
}

export type CookieState = Record<string, CookieStateItem>

/**
 * Consent-aware façade over a {@link CookieStore}. Before consent is granted,
 * writes are buffered in memory (and survive the SSR→client boundary via
 * `exportState`/`restoreState`); `enableConsent` flushes them to the store.
 */
export class CookieManager implements CookieInterface {
  private hasConsent = false
  private state: CookieState = {}

  constructor(private readonly store: CookieStore) {}

  restoreState(state: CookieState) {
    this.state = { ...this.state, ...reviveExpires(state) }
  }

  exportState() {
    return this.state
  }

  async set(name: string, value: string, options?: CookieSetOptions): Promise<void> {
    if (this.hasConsent || options?.ignoreConsent) {
      await this.store.set(name, value, toStoreOptions(options))
      return
    }
    this.state[name] = { name, value, options }
  }

  async get(name: string): Promise<string | undefined> {
    if (!this.hasConsent) {
      const state = this.state[name]
      if (state) {
        if (state.options?.expires?.isBefore(moment())) return undefined
        return state.value
      }
    }
    return this.store.get(name)
  }

  async remove(name: string): Promise<void> {
    delete this.state[name]
    await this.store.remove(name)
  }

  enableConsent() {
    this.hasConsent = true
    const items = Object.values(this.state)
    this.state = {}
    for (const item of items) {
      void this.store.set(item.name, item.value, toStoreOptions(item.options)).catch(e => {
        captureException(e, { level: 'warning', tags: { type: 'cookie' } })
      })
    }
  }

  static withConsent(store: CookieStore) {
    const manager = new CookieManager(store)
    manager.hasConsent = true
    return manager
  }
}

/**
 * Re-hydrate `expires` Moments after the state crosses the SSR→client boundary:
 * Redux serialization turns each Moment into an ISO string, which would break the
 * `.toDate()`/`.isBefore()` calls that assume a Moment.
 */
const reviveExpires = (state: CookieState): CookieState =>
  Object.fromEntries(
    Object.entries(state).map(([name, item]) => [
      name,
      item.options?.expires ? { ...item, options: { ...item.options, expires: moment(item.options.expires) } } : item,
    ])
  )

const toStoreOptions = (options?: CookieSetOptions): CookieStoreSetOptions => ({
  path: '/',
  httpOnly: options?.httpOnly,
  secure: options?.secure,
  sameSite: options?.sameSite,
  expires: options?.expires?.toDate(),
})
